import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { posApi, commissionApi } from '@/services/api';
import { companyDb, isLocalFirstMode } from '@/offline';
import { X, Loader2, ReceiptIcon, Banknote, Landmark } from 'lucide-react';
import BankAccountSelect from '@/components/common/BankAccountSelect';
import { useCartStore } from '@/store/cartStore';
import { useSyncStore } from '@/store/syncStore';
import { formatMoney, getCurrencySymbol, getActiveCurrency, roundMoney, getMoneyDecimals } from '@/utils/currency';
import { getCashNotesForCurrency, getCurrencyMeta } from '@/data/locales';
import { usePosKeyboardShortcuts } from '@/hooks/usePosKeyboardShortcuts';
import { usePosUiStore } from '@/store/posUiStore';
import ShortcutBadge from '@/components/pos/ShortcutBadge';

type PaymentMode = 'cash' | 'online' | 'both';

interface PaymentRow {
  payment_method: string;
  account: string;
  bank_account?: string;
  reference_no: string;
  amount: number;
}

interface Props {
  /** Cart total before write-off */
  netTotal: number;
  customerName: string;
  onComplete: (payments: PaymentRow[], writeOffAmount?: number, salesPerson?: string, invoiceDate?: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  shiftPocketName?: string;
  maxWriteOff?: number;
  /** When false, unpaid/partial is not allowed (kept for revert) */
  allowCreditSale?: boolean;
  enableCommission?: boolean;
  /** Error from parent complete-sale mutation */
  externalError?: string;
}

export type PaymentModalHandle = {
  submitSale: () => void;
};

const PaymentModal = forwardRef<PaymentModalHandle, Props>(function PaymentModal({
  netTotal,
  customerName,
  onComplete,
  onCancel,
  isLoading,
  shiftPocketName,
  maxWriteOff = 50,
  allowCreditSale = true,
  enableCommission = false,
  externalError,
}, ref) {
  const { t } = useTranslation();
  const shortcutBindings = usePosUiStore((s) => s.shortcuts);
  const { notes, setNotes } = useCartStore();
  const [mode, setMode] = useState<PaymentMode>('cash');
  const [cashAmount, setCashAmount] = useState(() => roundMoney(netTotal));
  const [onlineAmount, setOnlineAmount] = useState(0);

  const currencyCode = getActiveCurrency();
  const moneyDecimals = getMoneyDecimals(currencyCode);
  const formatMoneyInput = (val: number) => (val === 0 ? '' : roundMoney(val).toFixed(moneyDecimals));
  const cashNotes = useMemo(() => getCashNotesForCurrency(currencyCode), [currencyCode]);
  const currencyName = getCurrencyMeta(currencyCode).name;
  const noteStyles = [
    'bg-green-50 hover:bg-green-100 text-green-800 border-green-200',
    'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200',
    'bg-purple-50 hover:bg-purple-100 text-purple-800 border-purple-200',
    'bg-red-50 hover:bg-red-100 text-red-800 border-red-200',
    'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200',
    'bg-blue-50 hover:bg-blue-100 text-blue-800 border-blue-200',
    'bg-yellow-50 hover:bg-yellow-100 text-yellow-800 border-yellow-200',
    'bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border-indigo-200',
  ];
  const [selectedBank, setSelectedBank] = useState('');
  const [writeOff, setWriteOff] = useState(0);
  const [error, setError] = useState('');
  const [salesPerson, setSalesPerson] = useState('');
  const editingInvoiceDate = useCartStore((s) => s.editingInvoiceDate);
  const [invoiceDate, setInvoiceDate] = useState(() => {
    if (editingInvoiceDate) return editingInvoiceDate;
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split('T')[0];
  });

  const { data: salesPeople = [] } = useQuery({
    queryKey: ['salesPeople'],
    queryFn: async () => {
      const res = await commissionApi.listSalesPeople();
      return res.data.message as { user: string; full_name: string }[];
    },
    enabled: enableCommission,
    staleTime: 60_000,
  });

  const [activeField, setActiveField] = useState<'cash' | 'online' | 'writeOff'>('cash');
  const [cashTouched, setCashTouched] = useState(false);
  const [onlineTouched, setOnlineTouched] = useState(false);
  const [writeOffTouched, setWriteOffTouched] = useState(false);
  const [keypadInput, setKeypadInput] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle(ref, () => ({
    submitSale: () => formRef.current?.requestSubmit(),
  }));

  const syncKeypadInput = (field: 'cash' | 'online' | 'writeOff', value?: number) => {
    let val = 0;
    if (value !== undefined) {
      val = value;
    } else {
      if (field === 'cash') val = cashAmount;
      else if (field === 'online') val = onlineAmount;
      else if (field === 'writeOff') val = writeOff;
    }
    setKeypadInput(formatMoneyInput(val));
  };

  // BACKUP: previous shortfall-panel write-off flow (full-gap only).
  // Uncomment these + related handlers/UI below to revert the open write-off field.
  // const [shortfall, setShortfall] = useState<number | null>(null);
  // const [pendingPayments, setPendingPayments] = useState<PaymentRow[]>([]);

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const serverReachable = useSyncStore((s) => s.isServerReachable);
  const { data: pocketsData } = useQuery({
    queryKey: ['cashPockets', posSyncMode, serverReachable],
    queryFn: async () => {
      // Local modes / offline: IndexedDB only — never wait on API (axios hang).
      if (isLocalFirstMode() || !navigator.onLine || !useSyncStore.getState().isServerReachable) {
        const row = await companyDb().catalog_settings.get('cash_pockets');
        return (row?.value as unknown[]) || [];
      }
      try {
        const res = await posApi.getCashPockets();
        return res.data.message || [];
      } catch {
        const row = await companyDb().catalog_settings.get('cash_pockets');
        return (row?.value as unknown[]) || [];
      }
    },
  });
  const { data: banksData } = useQuery({
    queryKey: ['bankAccounts', posSyncMode, serverReachable],
    queryFn: async () => {
      if (isLocalFirstMode() || !navigator.onLine || !useSyncStore.getState().isServerReachable) {
        const row = await companyDb().catalog_settings.get('bank_accounts');
        return (row?.value as unknown[]) || [];
      }
      try {
        const res = await posApi.getBankAccounts();
        return res.data.message || [];
      } catch {
        const row = await companyDb().catalog_settings.get('bank_accounts');
        return (row?.value as unknown[]) || [];
      }
    },
  });

  const pockets = pocketsData || [];
  const banks = banksData || [];

  const defaultCashAccount = shiftPocketName
    || pockets.find((p: { is_default: boolean }) => p.is_default)?.pocket_name
    || 'Cash';

  const defaultBank =
    banks.find((b: { name: string; is_default: boolean }) => b.name === selectedBank)
    || banks.find((b: { is_default: boolean }) => b.is_default)
    || banks[0];

  const totalDue = roundMoney(netTotal);
  const writeOffApplied = Math.min(Math.max(0, writeOff), maxWriteOff, totalDue);
  const effectiveDue = roundMoney(Math.max(0, totalDue - writeOffApplied));

  useEffect(() => {
    setMode('cash');
    setCashAmount(totalDue);
    setOnlineAmount(0);
    setWriteOff(0);
    setError('');
    setActiveField('cash');
    setCashTouched(false);
    setOnlineTouched(false);
    setWriteOffTouched(false);
    setKeypadInput('');
  }, [totalDue]);

  useEffect(() => {
    if (selectedBank) return;
    const def = banks.find((b: { is_default: boolean }) => b.is_default) || banks[0];
    if (def?.name) setSelectedBank(def.name);
  }, [banks, selectedBank]);

  const buildPaymentsFromMode = (): PaymentRow[] => {
    if (mode === 'cash') {
      const applied = Math.min(Math.max(0, cashAmount), effectiveDue);
      return applied > 0 ? [{
        payment_method: 'Cash',
        account: defaultCashAccount,
        bank_account: '',
        reference_no: '',
        amount: applied,
      }] : [];
    }

    if (mode === 'online') {
      const applied = Math.min(Math.max(0, onlineAmount), effectiveDue);
      return applied > 0 ? [{
        payment_method: 'Bank',
        account: defaultBank?.bank_name || '',
        bank_account: defaultBank?.name || '',
        reference_no: '',
        amount: applied,
      }] : [];
    }

    const cashPay = Math.max(0, cashAmount);
    const onlinePay = Math.max(0, onlineAmount);
    const payments: PaymentRow[] = [];
    if (cashPay > 0) {
      payments.push({
        payment_method: 'Cash',
        account: defaultCashAccount,
        bank_account: '',
        reference_no: '',
        amount: cashPay,
      });
    }
    if (onlinePay > 0) {
      payments.push({
        payment_method: 'Bank',
        account: defaultBank?.bank_name || '',
        bank_account: defaultBank?.name || '',
        reference_no: '',
        amount: onlinePay,
      });
    }
    const total = payments.reduce((s, p) => s + p.amount, 0);
    if (total > effectiveDue + 0.01) {
      return payments;
    }
    return payments;
  };

  const paidAmount = buildPaymentsFromMode().reduce((s, p) => s + p.amount, 0);
  const remaining = effectiveDue - paidAmount;
  const cashChange = mode === 'cash' && cashAmount > effectiveDue ? cashAmount - effectiveDue : 0;

  const handleModeChange = (newMode: PaymentMode) => {
    setMode(newMode);
    setError('');
    setCashTouched(false);
    setOnlineTouched(false);
    setWriteOffTouched(false);
    if (newMode === 'cash') {
      setCashAmount(effectiveDue);
      setOnlineAmount(0);
      setActiveField('cash');
      setKeypadInput(formatMoneyInput(effectiveDue));
    } else if (newMode === 'online') {
      setCashAmount(0);
      setOnlineAmount(effectiveDue);
      setActiveField('online');
      setKeypadInput(formatMoneyInput(effectiveDue));
    } else {
      const half = roundMoney(effectiveDue / 2);
      setCashAmount(half);
      setOnlineAmount(roundMoney(effectiveDue - half));
      setActiveField('cash');
      setKeypadInput(formatMoneyInput(half));
    }
  };

  const cyclePaymentMode = () => {
    const order: PaymentMode[] = ['cash', 'online', 'both'];
    const idx = order.indexOf(mode);
    handleModeChange(order[(idx + 1) % order.length]);
  };

  usePosKeyboardShortcuts(
    {
      cyclePaymentMode,
      paymentModeCash: () => handleModeChange('cash'),
      paymentModeBank: () => handleModeChange('online'),
      paymentModeBoth: () => handleModeChange('both'),
      completeSale: () => formRef.current?.requestSubmit(),
      confirmPayment: () => formRef.current?.requestSubmit(),
      closeModal: onCancel,
    },
    true,
    undefined,
    ['cyclePaymentMode', 'paymentModeCash', 'paymentModeBank', 'paymentModeBoth', 'completeSale', 'confirmPayment', 'closeModal'],
  );

  const handleCashChange = (value: number) => {
    const amount = roundMoney(Math.max(0, value));
    setCashAmount(amount);
    if (mode === 'both') {
      setOnlineAmount(roundMoney(Math.max(0, effectiveDue - amount)));
    }
  };

  const handleOnlineChange = (value: number) => {
    const amount = roundMoney(Math.max(0, value));
    setOnlineAmount(amount);
    if (mode === 'both') {
      setCashAmount(roundMoney(Math.max(0, effectiveDue - amount)));
    }
  };

  const handleWriteOffChange = (value: number) => {
    setError('');
    const amount = Math.max(0, value);
    if (amount > maxWriteOff) {
      setWriteOff(maxWriteOff);
      setError(`Write off limited to ${formatMoney(maxWriteOff)} (Settings).`);
      return;
    }
    setWriteOff(Math.min(amount, totalDue));
  };

  const handleNoteClick = (value: number) => {
    const currentVal = activeField === 'cash' ? cashAmount : activeField === 'online' ? onlineAmount : writeOff;
    const touched = activeField === 'cash' ? cashTouched : activeField === 'online' ? onlineTouched : writeOffTouched;
    const base = touched ? currentVal : 0;

    const nextVal = base + value;
    if (activeField === 'cash') {
      setCashTouched(true);
      handleCashChange(nextVal);
    } else if (activeField === 'online') {
      setOnlineTouched(true);
      handleOnlineChange(nextVal);
    } else if (activeField === 'writeOff') {
      setWriteOffTouched(true);
      handleWriteOffChange(nextVal);
    }
    setKeypadInput(formatMoneyInput(nextVal));
  };

  const handleNumPadPress = (key: string) => {
    let currentInput = keypadInput;
    const touched = activeField === 'cash' ? cashTouched : activeField === 'online' ? onlineTouched : writeOffTouched;
    if (!touched) {
      currentInput = '';
      if (activeField === 'cash') setCashTouched(true);
      else if (activeField === 'online') setOnlineTouched(true);
      else if (activeField === 'writeOff') setWriteOffTouched(true);
    }

    let nextInput = currentInput;
    if (key === 'clear') {
      nextInput = '';
      if (activeField === 'cash') {
        setCashTouched(false);
        handleCashChange(0);
      } else if (activeField === 'online') {
        setOnlineTouched(false);
        handleOnlineChange(0);
      } else if (activeField === 'writeOff') {
        setWriteOffTouched(false);
        handleWriteOffChange(0);
      }
      setKeypadInput('');
      return;
    } else if (key === 'backspace') {
      nextInput = currentInput.slice(0, -1);
    } else if (key === '.') {
      if (!currentInput.includes('.')) {
        nextInput = currentInput === '' ? '0.' : currentInput + '.';
      }
    } else {
      nextInput = currentInput === '0' ? key : currentInput + key;
    }

    setKeypadInput(nextInput);

    const parsed = parseFloat(nextInput) || 0;
    if (activeField === 'cash') {
      handleCashChange(parsed);
    } else if (activeField === 'online') {
      handleOnlineChange(parsed);
    } else if (activeField === 'writeOff') {
      handleWriteOffChange(parsed);
    }
  };

  const handleExactAmount = () => {
    if (activeField === 'cash') {
      const exact = roundMoney(Math.max(0, effectiveDue - onlineAmount));
      setCashTouched(true);
      handleCashChange(exact);
      setKeypadInput(formatMoneyInput(exact));
    } else if (activeField === 'online') {
      const exact = roundMoney(Math.max(0, effectiveDue - cashAmount));
      setOnlineTouched(true);
      handleOnlineChange(exact);
      setKeypadInput(formatMoneyInput(exact));
    } else if (activeField === 'writeOff') {
      setWriteOffTouched(true);
      handleWriteOffChange(maxWriteOff);
      setKeypadInput(formatMoneyInput(maxWriteOff));
    }
  };

  const finalize = (payments: PaymentRow[], writeOffAmount?: number) => {
    onComplete(payments, writeOffAmount && writeOffAmount > 0 ? writeOffAmount : undefined, salesPerson || undefined, invoiceDate || undefined);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (writeOff > maxWriteOff) {
      setError(t('pos.writeOffExceeds', { amount: formatMoney(maxWriteOff) }));
      return;
    }

    if (mode === 'both' && cashAmount + onlineAmount > effectiveDue + 0.01) {
      setError(t('pos.cashBankExceed', { amount: formatMoney(effectiveDue) }));
      return;
    }

    if ((mode === 'online' || mode === 'both') && onlineAmount > 0 && !(selectedBank || defaultBank?.name)) {
      setError(t('pos.selectBankPayment'));
      return;
    }

    const payments = buildPaymentsFromMode();
    const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
    const gap = effectiveDue - totalPaid;

    if (payments.length === 0 && writeOffApplied <= 0 && effectiveDue > 0.01) {
      if (!allowCreditSale) {
        setError(t('pos.enterPaymentAmount'));
        return;
      }
      // Full unpaid
      finalize([], writeOffApplied);
      return;
    }

    if (gap > 0.01 && !allowCreditSale) {
      setError(t('pos.mustSettleFull', { amount: formatMoney(gap) }));
      return;
    }

    // Partial / unpaid allowed: save with whatever was paid; leftover stays on invoice
    finalize(payments, writeOffApplied);

    // BACKUP: old shortfall confirmation panel flow
    // if (gap > 0.01) {
    //   if (!allowCreditSale) {
    //     if (gap <= maxWriteOff) {
    //       setPendingPayments(payments);
    //       setShortfall(gap);
    //       return;
    //     }
    //     setError(`Walk in must be paid in full...`);
    //     return;
    //   }
    //   setPendingPayments(payments);
    //   setShortfall(gap);
    //   return;
    // }
    // finalize(payments);
  };

  const settledAmount = paidAmount + writeOffApplied;
  const statusHint =
    remaining <= 0.01
      ? 'Paid'
      : paidAmount > 0 || writeOffApplied > 0
        ? 'Partially Paid'
        : 'Unpaid';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg md:max-w-4xl max-h-[95vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">{t('pos.checkout')}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{customerName}</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              className="text-sm border border-gray-200 rounded px-2 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              title={t('pos.invoiceDate')}
            />
            <button type="button" onClick={onCancel} className="p-1.5 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* BACKUP: shortfall panel UI — restore with shortfall/pendingPayments state
          {shortfall !== null && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3 mb-4">
              ... Write off {formatMoney(shortfall)} / Save as Partially Paid / Unpaid ...
            </div>
          )}
          */}

          <div className="flex-1 overflow-y-auto p-5">

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-7 space-y-4">
                {enableCommission && salesPeople.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person</label>
                    <select
                      className="input-field"
                      value={salesPerson}
                      onChange={(e) => setSalesPerson(e.target.value)}
                    >
                      <option value="">Same as cashier</option>
                      {salesPeople.map((person) => (
                        <option key={person.user} value={person.user}>{person.full_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">{t('pos.paymentMode')}</span>
                  <div className="grid grid-cols-3 gap-3">
                    <label className={`flex flex-col items-center justify-center gap-1.5 p-3 border rounded-lg cursor-pointer text-sm transition-all ${mode === 'cash' ? 'border-primary-500 bg-primary-50 text-primary-800 ring-1 ring-primary-500' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                      <input type="radio" name="payment-mode" checked={mode === 'cash'} onChange={() => handleModeChange('cash')} className="sr-only" />
                      <Banknote className={`w-5 h-5 ${mode === 'cash' ? 'text-primary-600' : 'text-gray-500'}`} />
                      <span className="font-semibold">{t('pos.cash')}</span>
                      <ShortcutBadge binding={shortcutBindings.paymentModeCash} variant={mode === 'cash' ? 'tileActive' : 'tileIdle'} />
                    </label>

                    <label className={`flex flex-col items-center justify-center gap-1.5 p-3 border rounded-lg cursor-pointer text-sm transition-all ${mode === 'online' ? 'border-primary-500 bg-primary-50 text-primary-800 ring-1 ring-primary-500' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                      <input type="radio" name="payment-mode" checked={mode === 'online'} onChange={() => handleModeChange('online')} className="sr-only" />
                      <Landmark className={`w-5 h-5 ${mode === 'online' ? 'text-primary-600' : 'text-gray-500'}`} />
                      <span className="font-semibold">{t('pos.bank')}</span>
                      <ShortcutBadge binding={shortcutBindings.paymentModeBank} variant={mode === 'online' ? 'tileActive' : 'tileIdle'} />
                    </label>

                    <label className={`flex flex-col items-center justify-center gap-1.5 p-3 border rounded-lg cursor-pointer text-sm transition-all ${mode === 'both' ? 'border-primary-500 bg-primary-50 text-primary-800 ring-1 ring-primary-500' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}>
                      <input type="radio" name="payment-mode" checked={mode === 'both'} onChange={() => handleModeChange('both')} className="sr-only" />
                      <div className="flex items-center gap-1">
                        <Banknote className={`w-5 h-5 ${mode === 'both' ? 'text-primary-600' : 'text-gray-500'}`} />
                        <span className="text-[10px] text-gray-400">+</span>
                        <Landmark className={`w-5 h-5 ${mode === 'both' ? 'text-primary-600' : 'text-gray-500'}`} />
                      </div>
                      <span className="font-semibold">{t('pos.both')}</span>
                      <ShortcutBadge binding={shortcutBindings.paymentModeBoth} variant={mode === 'both' ? 'tileActive' : 'tileIdle'} />
                    </label>
                  </div>
                  
                  {(mode === 'online' || mode === 'both') && (
                    <div className="mt-4 p-4 border border-primary-100 bg-primary-50/30 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.selectBank')}</label>
                      <BankAccountSelect
                        value={selectedBank || defaultBank?.name || ''}
                        onChange={setSelectedBank}
                        banks={banks}
                        size="md"
                        required
                      />
                      {banks.length === 0 && (
                        <p className="text-[10px] text-amber-600 mt-1">{t('pos.noBanks')}</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg p-3 grid grid-cols-2 gap-4">
                  <div>
                    {mode === 'cash' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.receivedCash')}</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={cashAmount || ''}
                          onChange={(e) => handleCashChange(parseFloat(e.target.value) || 0)}
                          onFocus={() => {
                            setActiveField('cash');
                            syncKeypadInput('cash');
                          }}
                          className={`w-full text-sm border rounded px-3 py-2 transition-all ${activeField === 'cash'
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10'
                            : 'border-gray-200'
                            }`}
                        />
                      </div>
                    )}

                    {mode === 'online' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.amountBank')}</label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={onlineAmount || ''}
                          onChange={(e) => handleOnlineChange(parseFloat(e.target.value) || 0)}
                          onFocus={() => {
                            setActiveField('online');
                            syncKeypadInput('online');
                          }}
                          className={`w-full text-sm border rounded px-3 py-2 transition-all ${activeField === 'online'
                            ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10'
                            : 'border-gray-200'
                            }`}
                        />
                      </div>
                    )}

                    {mode === 'both' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.cashPayment')}</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={cashAmount || ''}
                            onChange={(e) => handleCashChange(parseFloat(e.target.value) || 0)}
                            onFocus={() => {
                              setActiveField('cash');
                              syncKeypadInput('cash');
                            }}
                            className={`w-full text-sm border rounded px-3 py-2 transition-all ${activeField === 'cash'
                              ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10'
                              : 'border-gray-200'
                              }`}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.bankTransfer')}</label>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={onlineAmount || ''}
                            onChange={(e) => handleOnlineChange(parseFloat(e.target.value) || 0)}
                            onFocus={() => {
                              setActiveField('online');
                              syncKeypadInput('online');
                            }}
                            className={`w-full text-sm border rounded px-3 py-2 transition-all ${activeField === 'online'
                              ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10'
                              : 'border-gray-200'
                              }`}
                          />
                        </div>
                        <p className="text-xs text-gray-500 pt-1 border-t border-gray-100">
                          Total: {formatMoney(cashAmount + onlineAmount)}
                          {Math.abs(cashAmount + onlineAmount - effectiveDue) > 0.01 && (
                            <span className="text-amber-600">
                              {' '}({cashAmount + onlineAmount >= effectiveDue ? 'over' : 'under'} by {formatMoney(Math.abs(cashAmount + onlineAmount - effectiveDue))})
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.writeOff')} ({getCurrencySymbol()})</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      max={Math.min(maxWriteOff, totalDue)}
                      value={writeOff || ''}
                      onChange={(e) => handleWriteOffChange(parseFloat(e.target.value) || 0)}
                      onFocus={() => {
                        setActiveField('writeOff');
                        syncKeypadInput('writeOff');
                      }}
                      className={`w-full text-sm border rounded px-3 py-2 transition-all ${activeField === 'writeOff'
                        ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10'
                        : 'border-gray-200'
                        }`}
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Max: {formatMoney(maxWriteOff)}
                    </p>
                  </div>


                </div>
                <div className="mb-6 pb-6 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('pos.invoiceNotes')}</label>
                  <textarea
                    className="w-full text-sm border rounded px-3 py-2 border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none bg-white"
                    rows={2}
                    value={notes || ''}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('pos.addNotes')}
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                  <div className="flex justify-between font-bold text-gray-900 text-lg">
                    <span>{t('pos.totalDue')}</span>
                    <span>{formatMoney(totalDue)}</span>
                  </div>
                  {writeOffApplied > 0 && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{t('pos.writeOff')}</span>
                      <span>− {formatMoney(writeOffApplied)}</span>
                    </div>
                  )}
                  {paidAmount > 0 && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{t('pos.paying')}</span>
                      <span>{formatMoney(paidAmount)}</span>
                    </div>
                  )}
                  {cashChange > 0 && (
                    <div className="flex justify-between text-sm font-medium text-amber-600">
                      <span>{t('pos.change')}</span>
                      <span>{formatMoney(cashChange)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-medium pt-1 border-t border-gray-200 mt-1">
                    <span className={remaining > 0.01 ? 'text-amber-600' : 'text-emerald-700'}>
                      {remaining > 0.01 ? t('pos.remaining') : t('pos.paid')}
                    </span>
                    <span className={remaining > 0.01 ? 'text-amber-600' : 'text-emerald-700'}>
                      {formatMoney(remaining > 0.01 ? remaining : settledAmount)}
                      {remaining > 0.01 && allowCreditSale && (
                        <span className="text-xs font-normal text-gray-500 ml-1">→ {statusHint}</span>
                      )}
                    </span>
                  </div>
                </div>

                {(error || externalError) && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error || externalError}</p>
                )}
              </div>

              {/* Right Column - Num Pad & Currency Notes */}
              <div className="hidden md:flex md:col-span-5 md:border-l border-gray-200 md:pl-6 flex-col space-y-4">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Entering: <span className="text-primary-600 font-bold">{activeField === 'cash' ? 'Cash Amount' : activeField === 'online' ? 'Bank Transfer' : 'Write-Off'}</span>
                  </span>
                  <button
                    type="button"
                    onClick={handleExactAmount}
                    className="text-xs font-bold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-2.5 py-1 rounded transition-colors"
                  >
                    {t('pos.setFullAmount')}
                  </button>
                </div>

                {/* Currency Notes Grid */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">{currencyName} notes</p>
                  <div className="grid grid-cols-3 gap-2">
                    {cashNotes.map((note, idx) => (
                      <button
                        key={note}
                        type="button"
                        onClick={() => handleNoteClick(note)}
                        className={`py-2.5 text-xs font-bold border rounded-lg transition-all shadow-sm active:scale-95 select-none ${
                          idx === cashNotes.length - 1 && cashNotes.length % 3 === 1 ? 'col-span-3' : ''
                        } ${noteStyles[idx % noteStyles.length]}`}
                      >
                        {formatMoney(note)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Numeric Keypad Grid */}
                <div className="flex flex-col mt-4 pt-2">
                  <div className="grid grid-cols-3 gap-2">
                    <button type="button" onClick={() => handleNumPadPress('7')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">7</button>
                    <button type="button" onClick={() => handleNumPadPress('8')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">8</button>
                    <button type="button" onClick={() => handleNumPadPress('9')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">9</button>

                    <button type="button" onClick={() => handleNumPadPress('4')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">4</button>
                    <button type="button" onClick={() => handleNumPadPress('5')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">5</button>
                    <button type="button" onClick={() => handleNumPadPress('6')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">6</button>

                    <button type="button" onClick={() => handleNumPadPress('1')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">1</button>
                    <button type="button" onClick={() => handleNumPadPress('2')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">2</button>
                    <button type="button" onClick={() => handleNumPadPress('3')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">3</button>

                    <button type="button" onClick={() => handleNumPadPress('clear')} className="py-3 text-lg font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors shadow-sm active:bg-red-200 select-none text-center">C</button>
                    <button type="button" onClick={() => handleNumPadPress('0')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">0</button>
                    <button type="button" onClick={() => handleNumPadPress('.')} className="py-3 text-lg font-semibold bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-800 transition-colors shadow-sm active:bg-gray-200 select-none text-center">.</button>

                    <button type="button" onClick={() => handleNumPadPress('backspace')} className="py-3 text-lg font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors shadow-sm active:bg-amber-200 select-none text-center">⌫</button>
                    <button type="button" onClick={handleExactAmount} className="col-span-2 py-3 text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm active:scale-95 select-none text-center">{t('pos.setFullAmount')}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
            <button type="button" onClick={onCancel} className="btn-secondary w-1/3 py-2.5">{t('common.cancel')}</button>
            <button type="submit" disabled={isLoading} className="btn-primary flex-1 py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptIcon className="w-4 h-4" />}
              {remaining > 0.01 && allowCreditSale
                ? `Save as ${statusHint}`
                : t('pos.completeSale')}
              <ShortcutBadge binding={shortcutBindings.completeSale} variant="primary" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default PaymentModal;